/* eslint-disable camelcase */
import { formatDateShort } from 'utils/dateUtils'
import { URL_CONST } from 'utils/urlConsts'

/**
 * Declarative spec for the NEW top-bar Notifications menu (IC-48830).
 *
 * Each spec entry describes how a single notification renders:
 *   {
 *     title:  string                      — bold heading
 *     text:   (data, ctx) => string|node  — body copy; {placeholders} come from
 *                                            state.app.data.notifications
 *     cta:    {
 *       label:  string                    — button / link text
 *       action: { type: 'mailto', href } | { type: 'route', to }
 *     }
 *     icon?:      ReactNode               — leading glyph; omit for a text-only row
 *     iconLabel?: string                  — accessible name for that glyph
 *     ctaOutside?: boolean                — link sits under the row, aligned with the icon
 *   }
 *
 * `data` is `state.app.data.notifications`. `ctx` carries extra app data the
 * copy may need (e.g. integration label for the mailbox-limit notification).
 *
 * Keys here are SPEC keys, not necessarily raw data keys. The menu
 * (NotificationsMenu.js) maps live notification data → these spec keys.
 * `re_integration_messages` (array of message ids 1..5) expands to one
 * RE_INTEGRATION_MESSAGE_* entry per id.
 */

const CONTACT_US = {
  label: 'Contact us',
  action: { type: 'mailto', href: 'mailto:support@ironscales.com' },
}

const REINTEGRATE_CTA = {
  label: 'Reintegrate',
  action: { type: 'route', to: URL_CONST.settings.emailServer },
}

const PASSWORD_EXPIRED_COPY = {
  title: 'Password Expired',
  text: 'Your password has expired. Please change it.',
}

export const hasPasswordExpiryWarning = (days) => typeof days === 'number' && !Number.isNaN(days)

export const getPasswordExpiryCopy = (days) => {
  if (hasPasswordExpiryWarning(days) && days <= 0) {
    return PASSWORD_EXPIRED_COPY
  }
  return {
    title: 'Password Expiring Soon',
    text: `Your password will expire in ${days} days.`,
  }
}

export const resolveNotificationTitle = (spec, data, ctx) =>
  typeof spec.title === 'function' ? spec.title(data, ctx) : spec.title

export const NOTIFICATION_SPECS = {
  license_expired: {
    title: 'License Expired',
    text: (data) => `Login access will be disabled on ${formatDateShort(data?.login_expiry)}.`,
    cta: CONTACT_US,
  },

  license_warn: {
    title: 'License Expiring Soon',
    text: (data) => `Your license will expire on ${formatDateShort(data?.license_expiery)}.`,
    cta: CONTACT_US,
  },

  warn_days_to_password_expire: {
    title: (data) => getPasswordExpiryCopy(data?.warn_days_to_password_expire).title,
    text: (data) => getPasswordExpiryCopy(data?.warn_days_to_password_expire).text,
    cta: {
      label: 'Change Password',
      action: { type: 'route', to: URL_CONST.settings.profile },
    },
  },

  license_limit_reached: {
    title: 'Mailbox Limit Reached',
    text: () => 'Some mailboxes may be unprotected - your active count exceeds your license limit.',
    cta: {
      label: 'Upgrade',
      action: { type: 'mailto', href: 'mailto:licenses@ironscales.com' },
    },
  },

  mailbox_911_disable_reason: {
    title: '911 Mailbox Disabled',
    text: () => 'Your 911 mailbox is disabled. Reconfigure it to restore full functionality.',
    cta: {
      label: 'Reconfigure',
      action: { type: 'route', to: URL_CONST.settings.emailServer },
    },
  },

  is_office365_old_oauth_scope: {
    title: 'Reintegration Recommended',
    text: () =>
      "We've improved our threat detection. Get better threat coverage with a quick reintegration",
    cta: REINTEGRATE_CTA,
  },

  re_integration_message_1: {
    title: 'Account Takeover Protection Inactive',
    text: () => 'Reintegrate your Office 365 account to restore protection.',
    cta: REINTEGRATE_CTA,
  },

  re_integration_message_2: {
    title: 'Teams Protection Inactive',
    text: () => 'Reintegrate your Office 365 account to restore protection.',
    cta: REINTEGRATE_CTA,
  },

  re_integration_message_3: {
    title: 'Multiple Protections Inactive',
    text: () =>
      'Reintegrate your Office 365 account to restore both Account Takeover and Teams protections.',
    cta: REINTEGRATE_CTA,
  },

  re_integration_message_4: {
    title: 'Integration Permissions Missing',
    text: () => 'Your Office 365 advanced integration is missing required permissions.',
    cta: REINTEGRATE_CTA,
  },

  // The backend emits id 5 (webapp/tests/test_account_status.py:1040); without a
  // spec the menu drops it and shows "No Notifications" (IC-52093).
  re_integration_message_5: {
    title: 'Integration Permissions Missing',
    text: () =>
      'Your Office 365 integration is missing required permissions. Please re-integrate it in the Email Server/Services settings to restore full functionality.',
    cta: REINTEGRATE_CTA,
  },

  deepfake: {
    title: 'New Deepfake Activity',
    text: () => 'Suspicious meeting activity detected since your last visit.',
    cta: {
      label: 'Investigate',
      // route built with the timestamp query param by the menu (see buildCtaTo)
      action: { type: 'route', to: URL_CONST.deepfake.meetings },
    },
  },

  // ---------------------------------------------------------------------------
  // Credits notifications (Confluence "Notifications" CTA table, 5 variants).
  //
  // backend trigger not emitted yet — state.app.data.notifications carries no
  // credits keys today (PLG-over / 80%-payg / 80%-prepurchase / 100%-payg /
  // 100%-prepurchase). The spec entries are defined so the menu renders them
  // the moment the backend starts emitting the corresponding data keys; until
  // then they are inert (no matching data → not rendered). Do NOT fabricate
  // data for these. See final report "backend gaps".
  // ---------------------------------------------------------------------------
  credits_plg_over: {
    title: 'Credits Limit Reached',
    text: () => 'You have used all of your included credits.',
    cta: {
      label: 'Add Credits',
      action: { type: 'route', to: URL_CONST.settings.creditsSettings },
    },
  },

  credits_80_payg: {
    title: 'Credits Running Low',
    text: () => "You've used 80% of your pay-as-you-go credits.",
    cta: {
      label: 'Add Credits',
      action: { type: 'route', to: URL_CONST.settings.creditsSettings },
    },
  },

  credits_80_prepurchase: {
    title: 'Credits Running Low',
    text: () => "You've used 80% of your prepurchased credits.",
    cta: {
      label: 'Add Credits',
      action: { type: 'route', to: URL_CONST.settings.creditsSettings },
    },
  },

  credits_100_payg: {
    title: 'Credits Limit Reached',
    text: () => "You've used 100% of your pay-as-you-go credits.",
    cta: {
      label: 'Add Credits',
      action: { type: 'route', to: URL_CONST.settings.creditsSettings },
    },
  },

  credits_100_prepurchase: {
    title: 'Credits Limit Reached',
    text: () => "You've used 100% of your prepurchased credits.",
    cta: {
      label: 'Add Credits',
      action: { type: 'route', to: URL_CONST.settings.creditsSettings },
    },
  },
}

const PSIMA_GENERATED_CARDS_DISMISS_KEY = 'psima_last_osint_run_cards_summary'
const PSIMA_LAUNCH_DELIVERY_DISMISS_KEY = 'psima_simulations_launch_and_delivery'

/** OSINT scan summary. Null when there is nothing to launch. */
export const formatPsimaGeneratedCards = (cards) => {
  if (!(cards?.total_cards_count > 0)) return null
  const levels = cards.level_counts || {}
  return {
    title: 'OSINT Scan complete',
    text: `OSINT Scan complete — ${cards.total_cards_count} new simulations ready (${levels.high}H / ${levels.medium}M / ${levels.low}L). Launch while they're current.`,
    ctaLabel: 'Review & launch',
    to: URL_CONST.psima.self,
    dismiss: {
      notification_key: PSIMA_GENERATED_CARDS_DISMISS_KEY,
      reference: cards.dismiss_reference,
    },
  }
}

/** One launched-simulation row. */
export const formatPsimaLaunchDelivery = (entry) => {
  if (!entry) return null
  const single = entry.campaign_count === 1
  const failedNote =
    entry.failed > 0
      ? single
        ? `, ${entry.failed} couldn't be delivered (credits refunded)`
        : `, ${entry.failed} failed (credits refunded)`
      : ''
  const text = single
    ? `${entry.title} is live — delivered to ${entry.delivered}/${entry.total}${failedNote}. Tracking is open.`
    : `${entry.campaign_count} simulations are live — ${entry.delivered}/${entry.total} delivered${failedNote}.`
  return {
    title: single ? entry.title : 'Simulations are live',
    text,
    ctaLabel: 'View',
    to: URL_CONST.psima.launched,
    dismiss: {
      notification_key: PSIMA_LAUNCH_DELIVERY_DISMISS_KEY,
      reference: entry.id,
    },
  }
}

/**
 * Spec keys that have no backend data trigger yet (see comment above).
 * Exported so callers/tests can reason about them explicitly.
 */
export const BACKEND_GAP_SPEC_KEYS = [
  'credits_plg_over',
  'credits_80_payg',
  'credits_80_prepurchase',
  'credits_100_payg',
  'credits_100_prepurchase',
]
